import React from 'react';
import { Button, Card, Row, Col, Typography, Space, Divider } from 'antd';
import { 
  TruckOutlined, 
  CheckCircleOutlined, 
  TeamOutlined, 
  DashboardOutlined,
  SecurityScanOutlined,
  RocketOutlined,
  ArrowRightOutlined,
  GlobalOutlined,
  MobileOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../utils/routes.config';
import './landing.css';

const { Title, Paragraph, Text } = Typography;

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <TruckOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      title: 'Real-Time Tracking',
      description: 'Track your deliveries in real-time with GPS integration and live status updates.'
    },
    {
      icon: <DashboardOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      title: 'Smart Dashboard',
      description: 'Comprehensive dashboard with analytics, order management, and performance metrics.'
    },
    {
      icon: <TeamOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      title: 'Driver Management',
      description: 'Efficiently manage drivers, assign orders, and monitor driver performance.'
    },
    {
      icon: <SecurityScanOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with API key authentication and encrypted data.'
    },
    {
      icon: <MobileOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      title: 'Mobile Ready',
      description: 'Fully responsive design that works seamlessly on all devices and platforms.'
    },
    {
      icon: <BarChartOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      title: 'Analytics & Reports',
      description: 'Get detailed insights with comprehensive reports and analytics dashboard.'
    }
  ];

  const benefits = [
    'Streamlined order management',
    'Automated driver assignment',
    'Real-time delivery tracking',
    'Multi-warehouse support',
    'Payment status tracking',
    'Complete order history'
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="container">
            <Space direction="vertical" size="large" align="center" style={{ width: '100%' }}>
              <div className="hero-badge">
                <RocketOutlined /> <span>Delivery Management Platform</span>
              </div>
              <Title level={1} className="hero-title">
                Streamline Your Delivery Operations
              </Title>
              <Paragraph className="hero-description">
                Powerful, intuitive delivery management system designed for modern businesses.
                Manage orders, track deliveries, and optimize your logistics operations all in one place.
              </Paragraph>
              <Space size="large" wrap>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate(routes.LOGIN)}
                  className="cta-button"
                >
                  Get Started
                </Button>
                <Button 
                  size="large" 
                  onClick={() => navigate(routes.API_DOCUMENTATION)}
                  className="cta-button-secondary"
                >
                  View Documentation
                </Button>
              </Space>
            </Space>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <Title level={2} style={{ marginBottom: 16 }}>
              Powerful Features for Modern Delivery Management
            </Title>
            <Paragraph style={{ fontSize: 18, color: '#666', maxWidth: 600, margin: '0 auto' }}>
              Everything you need to manage your delivery operations efficiently
            </Paragraph>
          </div>
          <Row gutter={[32, 32]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card 
                  className="feature-card"
                  hoverable
                  style={{ height: '100%', textAlign: 'center' }}
                >
                  <div style={{ marginBottom: 24 }}>
                    {feature.icon}
                  </div>
                  <Title level={4} style={{ marginBottom: 12 }}>
                    {feature.title}
                  </Title>
                  <Paragraph style={{ color: '#666', margin: 0 }}>
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <Title level={2} style={{ marginBottom: 24 }}>
                Why Choose Our Delivery Management System?
              </Title>
              <Paragraph style={{ fontSize: 16, color: '#666', marginBottom: 32 }}>
                Our platform is designed to help businesses of all sizes streamline their delivery operations,
                reduce costs, and improve customer satisfaction.
              </Paragraph>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {benefits.map((benefit, index) => (
                  <div key={index} className="benefit-item">
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20, marginRight: 12 }} />
                    <Text strong style={{ fontSize: 16 }}>{benefit}</Text>
                  </div>
                ))}
              </Space>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="benefits-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
                  <GlobalOutlined style={{ fontSize: 64, color: '#fff' }} />
                  <Title level={3} style={{ color: '#fff', margin: 0 }}>
                    Trusted by Businesses Worldwide
                  </Title>
                  <Paragraph style={{ color: '#fff', fontSize: 16, margin: 0 }}>
                    Join thousands of businesses using our platform to manage their delivery operations
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <Card className="cta-card">
            <Space direction="vertical" size="large" align="center" style={{ width: '100%' }}>
              <Title level={2} style={{ margin: 0, textAlign: 'center' }}>
                Ready to Transform Your Delivery Operations?
              </Title>
              <Paragraph style={{ fontSize: 18, color: '#666', textAlign: 'center', maxWidth: 600 }}>
                Start managing your deliveries more efficiently today. Get started in minutes.
              </Paragraph>
              <Button 
                type="primary" 
                size="large" 
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(routes.LOGIN)}
                className="cta-button"
                style={{ height: 50, fontSize: 16, paddingLeft: 32, paddingRight: 32 }}
              >
                Get Started Now
              </Button>
            </Space>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <Row gutter={[32, 32]}>
            <Col xs={24} sm={12} md={8}>
              <Title level={5}>Delivery Management</Title>
              <Paragraph style={{ color: '#999' }}>
                Streamline your delivery operations with our powerful platform.
              </Paragraph>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Title level={5}>Quick Links</Title>
              <Space direction="vertical" size="small">
                <a href="#" onClick={(e) => { e.preventDefault(); navigate(routes.LOGIN); }}>Login</a>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate(routes.API_DOCUMENTATION); }}>API Documentation</a>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Title level={5}>Support</Title>
              <Paragraph style={{ color: '#999' }}>
                Need help? Contact our support team for assistance.
              </Paragraph>
            </Col>
          </Row>
          <Divider />
          <div style={{ textAlign: 'center', color: '#999' }}>
            <Text>© 2024 Delivery Management System. All rights reserved.</Text>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;

